import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sun, Coffee, Utensils, Bus, Dumbbell, BookOpen, ClipboardList } from "lucide-react";

// Verified empowering NDIS lifestyle imagery (no clinical / aged-care cues).
const u = (id, w = 1200, q = 80) =>
  `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&w=${w}&q=${q}`;

const MOMENTS = [
  {
    id: "morning",
    time: "7:30",
    period: "AM",
    title: "Morning, on your own terms.",
    body: "Your routine, your pace. A quiet coffee, the news, or a slow start — whatever a good morning looks like for you.",
    Icon: Coffee,
    img: "/homes/coburg/1.jpg",
    alt: "Sun-lit Alpha home interior with timber floors and exposed brick — a calm morning",
    tag: "Daily living",
  },
  {
    id: "cooking",
    time: "12:15",
    period: "PM",
    title: "Cooking together.",
    body: "Lunch is rarely a chore here. We cook, plan and shop together — building skills with a support worker who actually listens.",
    Icon: Utensils,
    img: u("1556909114-f6e7ad7d3136", 1400, 82),
    alt: "Two adults cooking together in a bright kitchen",
    tag: "Skill-building",
  },
  {
    id: "community",
    time: "2:00",
    period: "PM",
    title: "Out in the community.",
    body: "Markets, the local café, art classes, a film. Community participation that matches what you actually want to do.",
    Icon: Bus,
    img: u("1517457373958-b7bdd4587205", 1800, 82),
    alt: "Diverse group of adults gathered together at an outdoor social",
    tag: "Community access",
    feature: true,
  },
  {
    id: "fitness",
    time: "4:00",
    period: "PM",
    title: "Movement that fits you.",
    body: "Pool, walking group, gym session, or a quiet stroll — wellbeing that's set by the participant, not the program.",
    Icon: Dumbbell,
    img: u("1571902943202-507ec2618e8f", 1400, 82),
    alt: "Bright modern gym with treadmills and free weights",
    tag: "Wellbeing",
  },
  {
    id: "relax",
    time: "7:30",
    period: "PM",
    title: "Evenings at home.",
    body: "A book, a podcast, dinner with a housemate. The home stays calm — low-stim lighting, soft acoustics, real privacy.",
    Icon: BookOpen,
    img: u("1554995207-c18c203602cb", 1400, 82),
    alt: "Warm contemporary living room with sofa, plants and natural light",
    tag: "At home",
  },
  {
    id: "coord",
    time: "Weekly",
    period: "",
    title: "A coordinator who knows you.",
    body: "One named coordinator. Plan reviews, allied health, paperwork — handled in plain English, never lost in a queue.",
    Icon: ClipboardList,
    img: u("1521791136064-7986c2920216", 1400, 82),
    alt: "Two people meeting and shaking hands in a warm professional setting",
    tag: "Support coordination",
  },
];

function MomentCard({ m, large = false }) {
  const Icon = m.Icon;
  return (
    <article
      className={`group relative overflow-hidden rounded-[20px] md:rounded-[26px] alpha-card !p-0 hover:-translate-y-1 transition-transform duration-500 alpha-reveal-stagger ${
        large ? "md:row-span-2 md:col-span-2" : ""
      }`}
      data-testid={`day-in-life-${m.id}`}
    >
      <div className={`relative ${large ? "aspect-[4/5] md:aspect-auto md:h-full" : "aspect-[4/3]"} overflow-hidden bg-[color:var(--alpha-bg-soft)]`}>
        <img
          src={m.img}
          alt={m.alt}
          loading="lazy"
          decoding="async"
          width={large ? 1800 : 1200}
          height={large ? 2250 : 900}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
        />
        {/* Editorial overlay — restrained */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0625]/85 via-[#0F0625]/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent to-[#0F0625]/15 mix-blend-multiply opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

        {/* Time chip — top-left editorial detail */}
        <div className="absolute top-4 left-4 inline-flex items-baseline gap-1 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur shadow-md">
          <span className="font-display text-[15px] font-semibold leading-none text-[color:var(--alpha-purple-700)]">{m.time}</span>
          {m.period && <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[color:var(--alpha-mute)]">{m.period}</span>}
        </div>

        {/* Tag — top-right */}
        <div className="absolute top-4 right-4">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/45 backdrop-blur border border-white/15 text-white text-[10px] tracking-[0.2em] font-bold uppercase">
            <Icon className="w-3 h-3" />
            {m.tag}
          </span>
        </div>

        {/* Bottom editorial copy */}
        <div className={`absolute inset-x-0 bottom-0 p-5 md:p-6 ${large ? "md:p-8 md:pb-9" : ""} text-white`}>
          <h3 className={`font-display font-medium tracking-tight !text-white leading-[1.08] ${large ? "text-[28px] md:text-[40px]" : "text-[20px] md:text-[22px]"}`}>
            {m.title}
          </h3>
          <p className={`mt-2 text-white/85 max-w-md ${large ? "text-[15.5px] md:text-base" : "text-[13.5px]"} leading-relaxed`}>
            {m.body}
          </p>
        </div>

        {/* Hover accent — soft warm lift */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
          style={{
            background: "radial-gradient(60% 50% at 50% 100%, rgba(134,89,199,0.35), transparent 70%)",
            mixBlendMode: "screen",
          }}
          aria-hidden
        />
      </div>
    </article>
  );
}

export default function DayInTheLife() {
  const feature = MOMENTS.find((m) => m.feature);
  const rest = MOMENTS.filter((m) => !m.feature);

  return (
    <section className="alpha-section-tight relative bg-white" data-testid="home-day-in-the-life">
      <div className="alpha-container">
        {/* Editorial header */}
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-7 md:mb-10 alpha-reveal">
          <div className="lg:col-span-7">
            <div className="alpha-overline inline-flex items-center gap-2">
              <Sun className="w-3.5 h-3.5" /> A day with Alpha
            </div>
            <h2 className="mt-3 font-display font-medium tracking-tight leading-[1.04] text-3xl sm:text-[44px] md:text-[52px]">
              Real life, <span className="alpha-serif-italic alpha-gradient-text">at your pace</span>.
            </h2>
            <p className="mt-3 text-[color:var(--alpha-mute)] max-w-xl text-[15.5px] leading-relaxed">
              Not a roster. A life. Mornings, meals, community, movement and rest — designed around the participant, supported by people who know them.
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[color:var(--alpha-mute)]">
              Six everyday moments · one good day
            </div>
            <Link
              to="/services"
              className="mt-3 alpha-btn-ghost text-sm !py-2.5 !px-4 inline-flex"
              data-testid="day-in-life-services"
            >
              Explore our supports <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Editorial 3-column grid · feature spans 2 rows */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 md:auto-rows-fr gap-3 md:gap-4 lg:gap-5">
          {/* Feature first on mobile, then mid-grid on desktop */}
          {feature && <MomentCard m={feature} large />}
          {rest.map((m) => (
            <MomentCard key={m.id} m={m} />
          ))}
        </div>

        {/* Closing editorial line */}
        <p className="mt-7 md:mt-9 text-center text-[13px] text-[color:var(--alpha-mute)] max-w-xl mx-auto leading-relaxed">
          Every day is shaped with the participant — not delivered to them. That's what good NDIS support looks like.
        </p>
      </div>
    </section>
  );
}
